import { defineStore } from 'pinia';
import { useStorage } from '@vueuse/core';
import type { MaybeRef } from 'vue';
import { OrderStatus } from '@/interfaces/order.interface';
import { notice } from '@/utils/notice.utils';
import { i18n } from '@/plugins/i18n';
import { useAuthStore } from './auth.store';
import { useUiStore } from './ui.store';
import { useOrderSummaryStore } from './orderSummary.store';
import router from '@/router';
import { useEcommerceStore } from './ecommerce.store';
import { EcommerceIntegrationStatus } from '@/interfaces/ecommerce.interface';

const DEFAULT_NOTICE_EXPIRATION = 1;

export enum NoticeId {
  SuspendedAccount = 'suspended-account',
  PaymentFailed = 'payment-failed',
  StalePlan = 'stale-plan',
  DowngradedPlan = 'downgraded-plan',
  EcommerceIntegrationFailed = 'ecommerce-integration-failed',
  EcommerceIntegrationInactive = 'ecommerce-integration-inactive',
}

export interface Notice {
  id: NoticeId | string;
  type: 'error' | 'info' | 'warning';
  message: string;
  dismissable: boolean;
  dismissed?: boolean;
  created_at: Date;
  expires_at?: Date | number;
  actions: {
    text: string;
    onClick: () => Promise<void> | void;
  }[];
}

interface NoticeState {
  notices: MaybeRef<Notice[]>;
}

function createMemoryStorage(): Storage {
  let store: Record<string, string> = {};

  return {
    get length() {
      return Object.keys(store).length;
    },
    clear() {
      store = {};
    },
    getItem(key: string) {
      return Object.prototype.hasOwnProperty.call(store, key) ? store[key] : null;
    },
    key(index: number) {
      return Object.keys(store)[index] ?? null;
    },
    removeItem(key: string) {
      delete store[key];
    },
    setItem(key: string, value: string) {
      store[key] = value;
    },
  };
}

const safeStorage =
  typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function'
    ? localStorage
    : createMemoryStorage();

export const useNoticeStore = defineStore('notice', {
  state: (): NoticeState => ({
    notices: useStorage<Notice[]>('notices', [], safeStorage, {
      serializer: {
        read: (v: string) => {
          const notices = JSON.parse(v);
          return notices.map((notice: Notice) => ({
            ...notice,
            created_at: new Date(notice.created_at),
            expires_at: notice.expires_at ? new Date(notice.expires_at) : undefined,
          }));
        },
        write: JSON.stringify,
      },
    }),
  }),
  getters: {
    /**
     * Gets the active notice.
     * @returns The active notice.
     */
    activeNotice: (state) => {
      const now = new Date();
      const activeNotices = state.notices.filter(
        (n) => !n.dismissed && (!n.expires_at || new Date(n.expires_at) > now),
      );

      // Define severity order: error (highest) -> warning -> info (lowest)
      const severityOrder = { error: 0, warning: 1, info: 2 };

      // Sort by severity and return the first one
      return activeNotices.sort((a, b) => severityOrder[a.type] - severityOrder[b.type])[0];
    },
  },
  actions: {
    /**
     * Checks and shows notices based on the current state.
     */
    checkNotices() {
      const authStore = useAuthStore();
      const ecommerceStore = useEcommerceStore();
      const orderSummaryStore = useOrderSummaryStore();

      //check if ecommerce integration is failed and show notice
      this.handleNotice(
        ecommerceStore.currentEcommerce?.integration?.status === EcommerceIntegrationStatus.FAILED,
        NoticeId.EcommerceIntegrationFailed,
        this.showEcommerceIntegrationFailedNotice,
      );

      //check if ecommerce integration is inactive and show notice
      this.handleNotice(
        ecommerceStore.currentEcommerce?.integration?.status ===
          EcommerceIntegrationStatus.INACTIVE,
        NoticeId.EcommerceIntegrationInactive,
        this.showEcommerceIntegrationInactiveNotice,
      );

      //check if account is suspended and show notice
      this.handleNotice(
        authStore.user?.current_order?.status === OrderStatus.SUSPENDED,
        NoticeId.SuspendedAccount,
        this.showSuspendedAccountNotice,
      );

      //check if payment failed and is NOT a trial client show notice
      this.handleNotice(
        orderSummaryStore.hasFailedActiveOrderSubscription && !orderSummaryStore.hasFreePlanActive,
        NoticeId.PaymentFailed,
        this.showPaymentFailedNotice,
      );

      //check if plan is stale and show notice
      this.handleNotice(
        orderSummaryStore.hasStalePlan && !orderSummaryStore.hasActiveOrderPlanChange,
        NoticeId.StalePlan,
        this.showStalePlanNotice,
      );

      //check if payment failed and is a trial client show notice
      this.handleNotice(
        orderSummaryStore.hasFailedActiveOrderSubscription && orderSummaryStore.hasFreePlanActive,
        NoticeId.DowngradedPlan,
        this.showDowngradedPlanNotice,
      );
    },
    /**
     * Adds a notice.
     * @param notice - The notice to add.
     */
    addNotice(notice: Omit<Notice, 'created_at'>) {
      //cleans any expired notices before adding a new one
      this.cleanExpiredNotices();

      //check if the notice already exists (not expired since its clean)
      const existingNoticeIdx = this.notices.findIndex((b) => b.id === notice.id);
      //if the notice already exists, update it in case the notice changed, also update the actions since localStorage cant store functions and return
      if (existingNoticeIdx !== -1) {
        return (this.notices[existingNoticeIdx] = {
          ...this.notices[existingNoticeIdx],
          actions: notice.actions,
          message: notice.message,
          type: notice.type,
          dismissable: notice.dismissable,
        });
      }

      //if the notice doesnt exist, add it
      const now = new Date();
      //if the expires_at is a Date object, use it, if its a number, convert it to days ex: 7 = 7days. Default to 7 days.
      const expires_at =
        notice.expires_at instanceof Date
          ? notice.expires_at
          : notice.expires_at
            ? new Date(now.getTime() + notice.expires_at * 24 * 60 * 60 * 1000)
            : new Date(now.getTime() + DEFAULT_NOTICE_EXPIRATION * 24 * 60 * 60 * 1000);

      this.notices.push({
        ...notice,
        created_at: now,
        expires_at,
      });
    },
    /**
     * Removes a notice.
     * @param notice_id - The notice ID to remove.
     */
    removeNotice(notice_id: NoticeId | string) {
      this.notices = this.notices.filter((b) => b.id !== notice_id);
    },
    /**
     * Dismisses a notice.
     * @param notice_id - The notice ID to dismiss.
     */
    dismissNotice(notice_id: NoticeId | string) {
      const targetNotice = this.notices.find((b) => b.id === notice_id);
      if (targetNotice) targetNotice.dismissed = true;
    },
    /**
     * Cleans expired notices.
     */
    cleanExpiredNotices() {
      const now = new Date();
      this.notices = this.notices.filter(
        (notice) => !notice.expires_at || new Date(notice.expires_at) > now,
      );
    },
    /**
     * Handles a notice based on a condition.
     * @param condition - The condition to check. If true, the callback is called.
     * @param noticeId - The notice ID to remove if the condition is false.
     * @param callback - The callback to call if the condition is true.
     */
    handleNotice(condition: boolean, noticeId: NoticeId, callback: () => void) {
      if (condition) {
        callback();
      } else {
        notice.remove(noticeId);
      }
    },

    //notices
    /**
     * Shows the suspended account notice.
     */
    showSuspendedAccountNotice() {
      const { t } = i18n.global as any;

      notice.error(t('notices.suspended_account.text'), {
        id: NoticeId.SuspendedAccount,
        dismissable: false,
        actions: [
          {
            text: t('notices.suspended_account.button'),
            onClick: async () => {
              router.push({ name: 'AccountSettingsBillingView' });
            },
          },
          {
            text: t('common.contact_support'),
            onClick: async () => {
              const uiStore = useUiStore();
              uiStore.openSupportDrawer();
            },
          },
        ],
      });
    },
    /**
     * Shows the payment failed notice.
     */
    showPaymentFailedNotice() {
      const { t } = i18n.global as any;
      notice.warning(t('notices.payment_failed.text'), {
        id: NoticeId.PaymentFailed,
        dismissable: false,
        actions: [
          {
            text: t('notices.payment_failed.button'),
            onClick: async () => {
              router.push({ name: 'AccountSettingsBillingView' });
            },
          },
        ],
      });
    },
    /**
     * Shows the stale plan notice.
     */
    showStalePlanNotice() {
      const { t } = i18n.global as any;
      notice.info(t('notices.stale_plan.text'), {
        id: NoticeId.StalePlan,
        dismissable: false,
        actions: [
          /*{
            text: '¿Qué significa esto?',
            onClick: async () => {
              router.push({ name: 'AccountSettingsPlanChangeView' });
            },
          },*/
          {
            text: t('notices.stale_plan.button'),
            onClick: async () => {
              router.push({ name: 'AccountSettingsPlanChangeView' });
            },
          },
        ],
      });
    },
    /**
     * Shows the downgraded plan notice.
     */
    showDowngradedPlanNotice() {
      const { t } = i18n.global as any;
      notice.warning(t('notices.downgraded_plan.text'), {
        id: NoticeId.DowngradedPlan,
        dismissable: false,
        actions: [
          {
            text: t('notices.downgraded_plan.button'),
            onClick: async () => {
              router.push({ name: 'CartSelectView' });
            },
          },
        ],
      });
    },
    /**
     * Shows the ecommerce integration failed notice.
     */
    showEcommerceIntegrationFailedNotice() {
      const { t } = i18n.global as any;
      const TIENDANUBE_APP_ID = import.meta.env.VITE_APP_TIENDANUBE_APP_ID;
      notice.error(t('notices.ecommerce_integration_failed_tiendanube.text'), {
        id: NoticeId.EcommerceIntegrationFailed,
        dismissable: false,
        actions: [
          {
            text: t('notices.ecommerce_integration_failed_tiendanube.button'),
            onClick: async () => {
              window.open(
                `https://www.tiendanube.com/apps/${TIENDANUBE_APP_ID}/authorize`,
                '_blank',
              );
            },
          },
          {
            text: t('notices.ecommerce_integration_failed_tiendanube.button_2'),
            onClick: async () => {
              window.open(
                `https://help.flokee.ai/conceptos-basicos/integraciones/tiendanube#qué-sucede-si-la-integración-falla`,
                '_blank',
              );
            },
          },
        ],
      });
    },
    /**
     * Shows the ecommerce integration inactive notice.
     */
    showEcommerceIntegrationInactiveNotice() {
      const { t } = i18n.global as any;
      const TIENDANUBE_APP_ID = import.meta.env.VITE_APP_TIENDANUBE_APP_ID;
      notice.error(t('notices.ecommerce_integration_inactive_tiendanube.text'), {
        id: NoticeId.EcommerceIntegrationFailed,
        dismissable: false,
        actions: [
          {
            text: t('notices.ecommerce_integration_inactive_tiendanube.button'),
            onClick: async () => {
              window.open(
                `https://www.tiendanube.com/apps/${TIENDANUBE_APP_ID}/authorize`,
                '_blank',
              );
            },
          },
          {
            text: t('notices.ecommerce_integration_inactive_tiendanube.button_2'),
            onClick: async () => {
              window.open(
                `https://help.flokee.ai/conceptos-basicos/integraciones/tiendanube#qué-sucede-si-la-integración-falla`,
                '_blank',
              );
            },
          },
        ],
      });
    },
  },
});
